import { z } from 'zod';
import { MediaNodeSchema } from './media';
import { getStorageUrl } from '../util';

export const SkinBgModeOptions = ['cover', 'contain', 'repeat'] as const;

export const GameSkinSchema = z.object({
	name: z.string().default('new skin'),

	skinRounding: z.enum(['none', 'small', 'medium', 'full']).default('none'),

	// title font, names this way for backwards compatibility
	skinFont: z.string().default(''),
	// font for regular text
	skinTextFont: z.string().default(''),

	skinLogo: MediaNodeSchema.optional(),

	skinBg: z.string().default('#ffffff'),
	skinBgImage: MediaNodeSchema.optional(),
	skinBgMode: z.enum(SkinBgModeOptions).default('repeat'),
	skinText: z.string().default('#000000'),

	skinBgButton: z.string().default('#000000'),
	skinBorderButton: z.string().default('#000000'),
	skinTextButton: z.string().default('#ffffff'),

	skinBgInput: z.string().default('#ffffff'),
	skinBorderInput: z.string().default('#000000'),
	skinTextInput: z.string().default('#000000'),

	skinBgBadge: z.string().default('#000000'),
	skinTextBadge: z.string().default('#ffffff'),
});

export type GameSkin = z.infer<typeof GameSkinSchema>;

export const SkinRecordSchema = z.object({
	id: z.string().uuid(),
	skin: GameSkinSchema,
	createdAt: z.string().datetime(),
	updatedAt: z.string().datetime(),
});

export type SkinRecord = z.infer<typeof SkinRecordSchema>;

export const SkinRecordUpdateSchema = z.object({
	id: z.string().uuid(),
	skin: GameSkinSchema,
});
export type SkinRecordUpdate = z.infer<typeof SkinRecordUpdateSchema>;

export function paletteFromSkin(skin: GameSkin) {
	const pal = new Set<string>();

	pal.add(skin.skinBg);
	pal.add(skin.skinText);

	pal.add(skin.skinBgButton);
	pal.add(skin.skinBorderButton);
	pal.add(skin.skinTextButton);

	pal.add(skin.skinBgBadge);
	pal.add(skin.skinTextBadge);

	pal.add(skin.skinBgInput);
	pal.add(skin.skinBorderInput);
	pal.add(skin.skinTextInput);

	return [...pal];
}

export function skinToCSSVariables(skin: GameSkin, storageZone: string): Record<string, string> {
	const rounding = {
		none: ['0', '0', '0', '0'],
		small: ['0.125rem', '0.187rem', '0.25rem', 'calc(infinity * 1px)'],
		medium: ['0.25rem', '0.375rem', '0.5rem', 'calc(infinity * 1px)'],
		// NOTE: not actually full rounding,
		// but this will effectively be full rounding for small elements while still making bigger elements look okay
		full: ['1.5rem', '1.5rem', '1.5rem', '1.5rem'],
	};

	const skinRounding = rounding[skin.skinRounding];

	return {
		'--skin-rounded-sm': skinRounding[0],
		'--skin-rounded-md': skinRounding[1],
		'--skin-rounded-lg': skinRounding[2],
		'--skin-rounded-full': skinRounding[3],

		'--skin-title-font': skin.skinFont ? `"${skin.skinFont}"` : 'inherit',
		'--skin-text-font': skin.skinTextFont ? `"${skin.skinTextFont}"` : 'inherit',

		'--skin-bg': skin.skinBg,
		'--skin-bg-img': skin.skinBgImage
			? `var(--skin-bg) url(${getStorageUrl(skin.skinBgImage, storageZone)}) ${
					skin.skinBgMode === 'repeat' ? 'repeat' : `center/${skin.skinBgMode}`
			  }`
			: skin.skinBg,
		'--skin-text': skin.skinText,

		'--skin-bg-button': skin.skinBgButton,
		'--skin-border-button': skin.skinBorderButton,
		'--skin-text-button': skin.skinTextButton,

		'--skin-bg-badge': skin.skinBgBadge,
		'--skin-text-badge': skin.skinTextBadge,

		'--skin-bg-input': skin.skinBgInput,
		'--skin-border-input': skin.skinBorderInput,
		'--skin-text-input': skin.skinTextInput,
	};
}
